import { useState, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import { getPagesBreakdown, getClicksBreakdown, getFormsBreakdown, getTrafficBreakdown } from '@/lib/api/external-api'
import { pickUniqueSubmissions } from '@/lib/api/formsFields'
import { useExperiments } from '@/hooks/useExperiments'
import { useURLState } from '@/hooks/useURLState'
import { useAuth } from '@/lib/contexts/AuthContext'
import { exportToCSV } from '@/lib/utils/export'
import { track } from '@/lib/utils/track'
import { sanitizeUrl } from '@/lib/utils/stringUtils'
import { HugeiconsIcon } from "@hugeicons/react"
import { Download04Icon, Loading01Icon } from "@hugeicons/core-free-icons"
import { PageShell, PageHeader } from "@/components/shared/layout"
import { formatRangeLabel } from "@/components/funnels/funnelListModel"
import { resolvePeriodWindow } from "@/lib/utils/periodWindow"
import { categorizeChannel } from '@/components/shared/ChannelsComponent'
import { computeLLMReferrals } from '@/components/shared/analytics/llmReferrals'

// ---------------------------------------------------------------------------
// Outlet context (matches DashboardLayout)
// ---------------------------------------------------------------------------

interface DashboardContext {
  propertyId?: string
  currentDomain?: string
  currentSite?: { domain?: string }
  token?: string
  period?: string
  filters?: any[]
  dashboardData?: any
  isLoading?: boolean
}

// ---------------------------------------------------------------------------
// Period conversion & display label
// ---------------------------------------------------------------------------

const toExternalApiPeriod = (period: string): string => {
  if (period.includes(':')) {
    const [start, end] = period.split(':')
    const startDate = new Date(start)
    const endDate = new Date(end)
    const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    let granularity = 'day'
    if (diffDays <= 2) granularity = 'hour'
    else if (diffDays > 90) granularity = 'week'
    return `${granularity}_CUSTOM_${start}_to_${end}`
  }
  const periodMap: Record<string, string> = {
    '1d': 'hour_PAST_24_H',
    '7d': 'day_PAST_7_D',
    '30d': 'day_PAST_30_D',
    '90d': 'day_PAST_3_M',
    '180d': 'week_PAST_6_M',
    '365d': 'week_PAST_12_M',
    '12m': 'month_PAST_12_M',
    'ytd': 'day_PAST_12_M',
    'mtd': 'day_PAST_30_D',
  }
  return periodMap[period] || 'day_PAST_7_D'
}

const PERIOD_LABELS: Record<string, string> = {
  '1d': '24 hours',
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  '180d': '6 months',
  '365d': '12 months',
  '12m': '12 months',
  'ytd': 'Year to date',
  'mtd': 'Month to date',
}

function periodLabel(period?: string): string {
  if (!period) return '7 days'
  if (PERIOD_LABELS[period]) return PERIOD_LABELS[period]
  if (period.includes(':')) {
    const [start, end] = period.split(':')
    return `${start} to ${end}`
  }
  return period
}

// ---------------------------------------------------------------------------
// Export data type definitions
// ---------------------------------------------------------------------------

type ExportKey =
  | 'trafficSummary'
  | 'pages'
  | 'devices'
  | 'browsers'
  | 'locations'
  | 'channels'
  | 'referrals'
  | 'utmData'
  | 'llmReferrals'
  | 'clicks'
  | 'forms'
  | 'abTests'

interface ExportType {
  key: ExportKey
  label: string
  description: string
  category: 'analytics' | 'marketing' | 'events' | 'testing'
  /** Whether this type requires an API call (vs client-side from dashboardData) */
  needsApi?: boolean
}

const EXPORT_TYPES: ExportType[] = [
  // Traffic
  { key: 'trafficSummary', label: 'Summary', description: 'Visitors, page views, bounce, time on site', category: 'analytics' },
  { key: 'pages', label: 'Pages', description: 'Every page with its visitors and exits', category: 'analytics', needsApi: true },
  { key: 'channels', label: 'Channels', description: 'Search, social, direct, referral, AI assistants', category: 'analytics' },
  { key: 'locations', label: 'Locations', description: 'Countries and cities', category: 'analytics' },
  { key: 'devices', label: 'Devices', description: 'Desktop, mobile, tablet and operating systems', category: 'analytics' },
  { key: 'browsers', label: 'Browsers', description: 'Chrome, Safari, Edge and more', category: 'analytics' },
  // Marketing
  { key: 'referrals', label: 'Referrers', description: 'Sites that sent visitors', category: 'marketing' },
  { key: 'utmData', label: 'UTM campaigns', description: 'Sources, mediums and campaigns as three files', category: 'marketing' },
  { key: 'llmReferrals', label: 'AI assistants', description: 'Visits from ChatGPT, Claude, Perplexity and others', category: 'marketing' },
  // Behavior and tests
  { key: 'clicks', label: 'Clicks', description: 'Every tracked click by page and element', category: 'events', needsApi: true },
  { key: 'forms', label: 'Form submissions', description: 'Submissions per form and page', category: 'events', needsApi: true },
  { key: 'abTests', label: 'A/B tests', description: 'Every test, version and result', category: 'testing' },
]

const CATEGORIES = [
  { id: 'analytics' as const, label: 'Traffic', members: ['analytics'] },
  { id: 'marketing' as const, label: 'Marketing', members: ['marketing'] },
  { id: 'events' as const, label: 'Behavior and tests', members: ['events', 'testing'] },
]

// ---------------------------------------------------------------------------
// Data transformation helpers
// ---------------------------------------------------------------------------

function transformTrafficSummary(dashboardData: any): Record<string, unknown>[] {
  const metrics = dashboardData?.metrics
  if (!metrics) return []

  const aggregate = metrics.aggregate || []
  const totalVisitors = aggregate
    .filter((r: any) => r.referer_group_aggregate)
    .reduce((sum: number, r: any) => sum + (Number(r.count) || 0), 0)

  const stats = dashboardData?.summary || {}
  return [{
    visitors: totalVisitors || stats.visitors || 0,
    page_views: stats.pageViews || stats.page_views || 0,
    sessions: stats.sessions || totalVisitors || 0,
    bounce_rate: stats.bounceRate ?? stats.bounce_rate ?? '',
    avg_duration_seconds: stats.avgDuration ?? stats.avg_duration ?? '',
  }]
}

function transformDevices(breakdown: any): Record<string, unknown>[] {
  return (breakdown?.data?.os || [])
    .slice()
    .sort((a: any, b: any) => (b.sessions || 0) - (a.sessions || 0))
    .map((r: any) => ({ os: r.os || 'Unknown', sessions: r.sessions || 0 }))
}

function transformBrowsers(breakdown: any): Record<string, unknown>[] {
  return (breakdown?.data?.browsers || [])
    .slice()
    .sort((a: any, b: any) => (b.sessions || 0) - (a.sessions || 0))
    .map((r: any) => ({ browser: r.browser || 'Unknown', sessions: r.sessions || 0 }))
}

function transformLocations(breakdown: any): Record<string, unknown>[] {
  return (breakdown?.data?.countries || [])
    .slice()
    .sort((a: any, b: any) => (b.sessions || 0) - (a.sessions || 0))
    .map((r: any) => ({ country: r.country || 'Unknown', sessions: r.sessions || 0 }))
}

function transformChannels(dashboardData: any): Record<string, unknown>[] {
  const aggregate = dashboardData?.metrics?.aggregate || []
  const stats: Record<string, number> = {}
  aggregate
    .filter((r: any) => r.referer_group_aggregate)
    .forEach((r: any) => {
      const referrer = r.referer_group
      const count = Number(r.count) || 0
      const channel = categorizeChannel(referrer, r.utm_medium, r.utm_source)
      stats[channel] = (stats[channel] || 0) + count
    })
  const total = Object.values(stats).reduce((s, v) => s + v, 0)
  return Object.entries(stats)
    .sort(([, a], [, b]) => b - a)
    .map(([channel, count]) => ({
      channel,
      sessions: count,
      percentage: total > 0 ? ((count / total) * 100).toFixed(1) : '0.0',
    }))
}


function transformReferrals(dashboardData: any): Record<string, unknown>[] {
  const aggregate = dashboardData?.metrics?.aggregate || []
  const stats: Record<string, number> = {}
  aggregate
    .filter((r: any) => r.referer_group_aggregate)
    .forEach((r: any) => {
      const name = r.referer_group ? sanitizeUrl(r.referer_group) : 'Direct'
      stats[name] = (stats[name] || 0) + (Number(r.count) || 0)
    })
  return Object.entries(stats)
    .sort(([, a], [, b]) => b - a)
    .map(([referrer, count]) => ({ referrer, sessions: count }))
}

function transformUTMData(dashboardData: any): { sources: Record<string, unknown>[]; campaigns: Record<string, unknown>[]; mediums: Record<string, unknown>[] } {
  const utmResults = dashboardData?.metrics?.utmResults || []
  const sources: Record<string, number> = {}
  const campaigns: Record<string, number> = {}
  const mediums: Record<string, number> = {}

  utmResults.forEach((d: any) => {
    const count = parseInt(d.count) || 0
    if (d.utm_source_aggregate === 'utm_source' && d.utm_source) sources[d.utm_source] = (sources[d.utm_source] || 0) + count
    if (d.utm_campaign_aggregate === 'utm_campaign' && d.utm_campaign) campaigns[d.utm_campaign] = (campaigns[d.utm_campaign] || 0) + count
    if (d.utm_medium_aggregate === 'utm_medium' && d.utm_medium) mediums[d.utm_medium] = (mediums[d.utm_medium] || 0) + count
  })

  const toRows = (stats: Record<string, number>, key: string) =>
    Object.entries(stats)
      .sort(([, a], [, b]) => b - a)
      .map(([name, count]) => ({ [key]: name, sessions: count }))

  return {
    sources: toRows(sources, 'source'),
    campaigns: toRows(campaigns, 'campaign'),
    mediums: toRows(mediums, 'medium'),
  }
}

// Same rule as the AI assistants panel on Traffic sources, so the file matches the page.
function transformLLMReferrals(dashboardData: any): Record<string, unknown>[] {
  return computeLLMReferrals(dashboardData).llmData.map((r) => ({ provider: r.name, domain: r.domain, visits: r.count }))
}

function transformPages(data: any): Record<string, unknown>[] {
  if (!data?.data) return []
  return data.data.map((p: any) => ({
    page: p.page,
    page_views: p.page_views,
    unique_visitors: p.unique_visitors,
    avg_session_length: p.avg_session_length,
    avg_scroll_percent: p.avg_scroll_percent,
    bounce_rate: p.bounce_rate,
  }))
}

function transformClicks(data: any): Record<string, unknown>[] {
  if (!data?.data) return []
  return data.data.map((p: any) => {
    const row: Record<string, unknown> = {
      page: p.page,
      page_group: p.page_group ?? '',
      total_clicks: p.total_clicks,
      unique_sessions: p.unique_sessions,
    }
    const targets: any[] = p.top_targets || []
    for (let i = 0; i < 5; i++) {
      row[`target_${i + 1}`] = targets[i]?.target ?? ''
      row[`target_${i + 1}_clicks`] = targets[i]?.clicks ?? ''
    }
    return row
  })
}

function transformForms(data: any): Record<string, unknown>[] {
  if (!data?.data) return []
  return data.data.map((p: any) => {
    const unique = pickUniqueSubmissions(p)
    return {
      page: p.page,
      total_submissions: p.total_submissions,
      unique_submissions: unique,
      unique_sessions: unique,
    }
  })
}

function transformExperiments(experiments: any[]): Record<string, unknown>[] {
  if (!experiments || experiments.length === 0) return []
  const rows: Record<string, unknown>[] = []
  experiments.forEach((exp) => {
    (exp.variants || []).forEach((v: any) => {
      rows.push({
        experiment: exp.experimentName,
        status: exp.status,
        variant: v.label,
        url: v.url,
        is_control: v.isControl ? 'Yes' : 'No',
        sessions: v.sessions ?? '',
        conversions: v.conversions ?? '',
        conversion_rate: v.conversionRate != null ? `${v.conversionRate.toFixed(2)}%` : '',
        bounce_rate: v.bounceRate != null ? `${v.bounceRate.toFixed(2)}%` : '',
        statistical_significance: exp.statisticalSignificance != null ? `${exp.statisticalSignificance}%` : '',
        winning_variant: exp.winningVariant || '',
      })
    })
  })
  return rows
}

// ---------------------------------------------------------------------------
// Row count estimators (for the summary panel)
// ---------------------------------------------------------------------------

function estimateRowCount(key: ExportKey, dashboardData: any, experiments: any[] | undefined): string {
  switch (key) {
    case 'trafficSummary': return '1 row'
    case 'devices': return 'varies'
    case 'browsers': return 'varies'
    case 'locations': return 'varies'
    case 'channels': return `${transformChannels(dashboardData).length} rows`
    case 'referrals': return `${transformReferrals(dashboardData).length} rows`
    case 'utmData': {
      const { sources, campaigns, mediums } = transformUTMData(dashboardData)
      return `${sources.length + campaigns.length + mediums.length} rows (3 files)`
    }
    case 'llmReferrals': return `${transformLLMReferrals(dashboardData).length} rows`
    case 'pages': return 'varies'
    case 'clicks': return 'varies'
    case 'forms': return 'varies'
    case 'abTests': {
      const rows = transformExperiments(experiments || [])
      return rows.length > 0 ? `${rows.length} rows` : '0 rows'
    }
    default: return ''
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Export() {
  const ctx = useOutletContext<DashboardContext>()
  const dashboardData = ctx?.dashboardData
  const isLoading = ctx?.isLoading
  const propertyId = ctx?.propertyId

  const { state } = useURLState()
  const { token: authToken } = useAuth()
  const token = ctx?.token || authToken
  const period = state.period || '7d'

  const { data: experiments } = useExperiments({ propertyId })

  const [selected, setSelected] = useState<Record<ExportKey, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    EXPORT_TYPES.forEach((t) => { initial[t.key] = true })
    return initial as Record<ExportKey, boolean>
  })

  const [isExporting, setIsExporting] = useState(false)

  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected])

  const toggle = (key: ExportKey, checked: boolean) => {
    setSelected((prev) => ({ ...prev, [key]: checked }))
  }

  const selectAll = () => {
    const next: Record<string, boolean> = {}
    EXPORT_TYPES.forEach((t) => { next[t.key] = true })
    setSelected(next as Record<ExportKey, boolean>)
  }

  const deselectAll = () => {
    const next: Record<string, boolean> = {}
    EXPORT_TYPES.forEach((t) => { next[t.key] = false })
    setSelected(next as Record<ExportKey, boolean>)
  }

  // Compute file count (UTM creates 3 files)
  const fileCount = useMemo(() => {
    let count = 0
    EXPORT_TYPES.forEach((t) => {
      if (!selected[t.key]) return
      if (t.key === 'utmData') count += 3
      else count += 1
    })
    return count
  }, [selected])

  // ----- Export execution -----

  const handleExport = async () => {
    if (selectedCount === 0) {
      toast.error('Please select at least one data type to export')
      return
    }
    if (!propertyId || !token) {
      toast.error('Missing required data. Please try refreshing the page.')
      return
    }

    setIsExporting(true)
    let downloadCount = 0

    try {
      // 1. Fetch API-backed types in parallel
      const externalApiPeriod = toExternalApiPeriod(period)
      const apiPromises: Record<string, Promise<any>> = {}
      if (selected.pages) apiPromises.pages = getPagesBreakdown(externalApiPeriod, propertyId, token)
      if (selected.clicks) apiPromises.clicks = getClicksBreakdown(externalApiPeriod, propertyId, token)
      if (selected.forms) apiPromises.forms = getFormsBreakdown(externalApiPeriod, propertyId, token)
      // Always fetch breakdown for devices/browsers/locations — never rely on stale dashboardData
      apiPromises.breakdown = getTrafficBreakdown(externalApiPeriod, propertyId, token)

      const apiKeys = Object.keys(apiPromises)
      const apiResults = await Promise.allSettled(Object.values(apiPromises))
      const apiData: Record<string, any> = {}
      apiKeys.forEach((key, i) => {
        const result = apiResults[i]
        if (result.status === 'fulfilled') apiData[key] = result.value
      })

      // 2. Build all CSV exports
      const downloads: { filename: string; rows: Record<string, unknown>[] }[] = []

      if (selected.trafficSummary) {
        const rows = transformTrafficSummary(dashboardData)
        if (rows.length) downloads.push({ filename: 'traffic-summary.csv', rows })
      }

      if (selected.pages && apiData.pages) {
        const rows = transformPages(apiData.pages)
        if (rows.length) downloads.push({ filename: 'pages.csv', rows })
      }

      if (selected.devices) {
        const rows = transformDevices(apiData.breakdown)
        if (rows.length) downloads.push({ filename: 'devices.csv', rows })
      }

      if (selected.browsers) {
        const rows = transformBrowsers(apiData.breakdown)
        if (rows.length) downloads.push({ filename: 'browsers.csv', rows })
      }

      if (selected.locations) {
        const rows = transformLocations(apiData.breakdown)
        if (rows.length) downloads.push({ filename: 'locations.csv', rows })
      }

      if (selected.channels) {
        const rows = transformChannels(dashboardData)
        if (rows.length) downloads.push({ filename: 'channels.csv', rows })
      }

      if (selected.referrals) {
        const rows = transformReferrals(dashboardData)
        if (rows.length) downloads.push({ filename: 'referrals.csv', rows })
      }

      if (selected.utmData) {
        const { sources, campaigns, mediums } = transformUTMData(dashboardData)
        if (sources.length) downloads.push({ filename: 'utm-sources.csv', rows: sources })
        if (campaigns.length) downloads.push({ filename: 'utm-campaigns.csv', rows: campaigns })
        if (mediums.length) downloads.push({ filename: 'utm-mediums.csv', rows: mediums })
      }

      if (selected.llmReferrals) {
        const rows = transformLLMReferrals(dashboardData)
        if (rows.length) downloads.push({ filename: 'llm-referrals.csv', rows })
      }

      if (selected.clicks && apiData.clicks) {
        const rows = transformClicks(apiData.clicks)
        if (rows.length) downloads.push({ filename: 'clicks.csv', rows })
      }

      if (selected.forms && apiData.forms) {
        const rows = transformForms(apiData.forms)
        if (rows.length) downloads.push({ filename: 'forms.csv', rows })
      }

      if (selected.abTests) {
        const rows = transformExperiments(experiments || [])
        if (rows.length) downloads.push({ filename: 'ab-tests.csv', rows })
      }

      // 3. Download with staggered delay
      for (const dl of downloads) {
        exportToCSV(dl.filename, dl.rows)
        downloadCount++
        if (downloadCount < downloads.length) {
          await new Promise((r) => setTimeout(r, 150))
        }
      }

      if (downloadCount > 0) {
        track('data_exported', token, { export_type: 'csv', row_count: downloadCount })
        toast.success(`${downloadCount} file${downloadCount > 1 ? 's' : ''} exported as CSV`)
      } else {
        toast.warning('No data available to export for the selected criteria')
      }
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // ----- Render -----

  const filenameFor = (key: ExportKey): string[] =>
    key === 'utmData' ? ['utm-sources.csv', 'utm-campaigns.csv', 'utm-mediums.csv']
      : key === 'trafficSummary' ? ['traffic-summary.csv']
        : key === 'llmReferrals' ? ['llm-referrals.csv']
          : key === 'abTests' ? ['ab-tests.csv']
            : [`${key}.csv`]
  const selectedFiles = EXPORT_TYPES.filter((t) => selected[t.key]).flatMap((t) => filenameFor(t.key))
  const range = resolvePeriodWindow(period)
  const rangeLabel = (range && formatRangeLabel(range.start, range.end)) || periodLabel(period)
  const site = (ctx?.currentSite?.domain || ctx?.currentDomain || '').replace(/^https?:\/\//, '').replace(/^www\./, '')

  return (
    <PageShell>
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_336px]">
        <div className="flex min-w-0 flex-col">
          <PageHeader title="Export" description="Pick what you need. Each one downloads as its own CSV file." />
          <div className="well flex flex-col gap-2">
            {CATEGORIES.map((c) => {
              const types = EXPORT_TYPES.filter((t) => (c.members as string[]).includes(t.category))
              const picked = types.filter((t) => selected[t.key]).length
              return (
                <section key={c.id} className="surface-raised flex flex-col overflow-clip [&>*+*]:border-t [&>*+*]:border-border">
                  <div className="flex items-center justify-between px-[18px] py-3.5">
                    <h2 className="text-sm font-semibold leading-[18px]">{c.label}</h2>
                    <span className="text-xs leading-4 text-foreground/60 tabular-nums">{picked} of {types.length}</span>
                  </div>
                  {types.map((t) => (
                    <label key={t.key} className="flex min-h-12 cursor-pointer items-center gap-3 px-[18px] py-2.5 transition-colors hover:bg-muted/40">
                      <Checkbox checked={selected[t.key]} onCheckedChange={(checked) => toggle(t.key, checked as boolean)} />
                      <span className="w-40 shrink-0 text-sm font-medium leading-[18px]">{t.label}</span>
                      <span className="hidden min-w-0 flex-1 truncate text-xs leading-4 text-foreground/60 sm:block">{t.description}</span>
                      <span className="ml-auto shrink-0 text-xs leading-4 text-foreground/60 tabular-nums">
                        {estimateRowCount(t.key, dashboardData, experiments)}
                      </span>
                    </label>
                  ))}
                </section>
              )
            })}
          </div>
        </div>

        <aside className="well lg:sticky lg:top-6">
          <div className="surface-raised flex flex-col gap-4 p-5">
            <h2 className="text-sm font-semibold leading-[18px]">Your export</h2>
            <dl className="flex flex-col text-[13px] leading-4 [&>div+div]:border-t [&>div+div]:border-border">
              {[
                ['Dates', rangeLabel],
                ...(site ? [['Site', site]] : []),
                ['Files', `${fileCount} CSV ${fileCount === 1 ? 'file' : 'files'}`],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
                  <dt className="text-foreground/60">{k}</dt>
                  <dd className="truncate font-medium tabular-nums">{v}</dd>
                </div>
              ))}
            </dl>
            {selectedFiles.length > 0 && (
              <div className="flex flex-col gap-1.5 rounded-lg bg-canvas px-3 py-3 font-mono text-xs leading-4">
                {selectedFiles.slice(0, 6).map((f) => <span key={f} className="truncate">{f}</span>)}
                {selectedFiles.length > 6 && (
                  <span className="font-sans text-foreground/60">and {selectedFiles.length - 6} more</span>
                )}
              </div>
            )}
            <div className="flex flex-col items-center gap-2">
              <Button
                onClick={handleExport}
                disabled={isExporting || isLoading || selectedCount === 0}
                className="h-9 w-full gap-1.5 text-[13px]"
              >
                {isExporting ? (
                  <>
                    <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
                    Downloading
                  </>
                ) : (
                  <>
                    <HugeiconsIcon icon={Download04Icon} size={14} />
                    {fileCount > 0 ? `Download ${fileCount} ${fileCount === 1 ? 'file' : 'files'}` : 'Pick something to export'}
                  </>
                )}
              </Button>
              <div className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-foreground/60" onClick={selectAll}>
                  Select all
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-foreground/60" onClick={deselectAll}>
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </PageShell>
  )
}
